// Mirror of Portal-BE/app/integrations/services/phoneFormat.js.
// Keep these in sync — single source of truth conceptually, two
// files because the BE is plain ESM and the FE is TypeScript.

export type CountryFormat = {
    dialCode: string;
    minLen: number;
    maxLen: number;
    mobileExample: string;
    name: string;
};

export const EU_PHONE_FORMATS: Record<string, CountryFormat> = {
    AT: { dialCode: "+43",  minLen: 10, maxLen: 13, mobileExample: "6641234567",   name: "Austria" },
    BE: { dialCode: "+32",  minLen: 9,  maxLen: 9,  mobileExample: "470123456",    name: "Belgium" },
    BG: { dialCode: "+359", minLen: 9,  maxLen: 9,  mobileExample: "881234567",    name: "Bulgaria" },
    HR: { dialCode: "+385", minLen: 8,  maxLen: 9,  mobileExample: "912345678",    name: "Croatia" },
    CY: { dialCode: "+357", minLen: 8,  maxLen: 8,  mobileExample: "96123456",     name: "Cyprus" },
    CZ: { dialCode: "+420", minLen: 9,  maxLen: 9,  mobileExample: "601123456",    name: "Czech Republic" },
    DK: { dialCode: "+45",  minLen: 8,  maxLen: 8,  mobileExample: "20123456",     name: "Denmark" },
    EE: { dialCode: "+372", minLen: 7,  maxLen: 8,  mobileExample: "51234567",     name: "Estonia" },
    FI: { dialCode: "+358", minLen: 9,  maxLen: 10, mobileExample: "412345678",    name: "Finland" },
    FR: { dialCode: "+33",  minLen: 9,  maxLen: 9,  mobileExample: "612345678",    name: "France" },
    DE: { dialCode: "+49",  minLen: 10, maxLen: 11, mobileExample: "15123456789",  name: "Germany" },
    GR: { dialCode: "+30",  minLen: 10, maxLen: 10, mobileExample: "6941234567",   name: "Greece" },
    HU: { dialCode: "+36",  minLen: 9,  maxLen: 9,  mobileExample: "201234567",    name: "Hungary" },
    IE: { dialCode: "+353", minLen: 9,  maxLen: 9,  mobileExample: "851234567",    name: "Ireland" },
    IT: { dialCode: "+39",  minLen: 9,  maxLen: 11, mobileExample: "3123456789",   name: "Italy" },
    LV: { dialCode: "+371", minLen: 8,  maxLen: 8,  mobileExample: "21234567",     name: "Latvia" },
    LT: { dialCode: "+370", minLen: 8,  maxLen: 8,  mobileExample: "61234567",     name: "Lithuania" },
    LU: { dialCode: "+352", minLen: 6,  maxLen: 9,  mobileExample: "621123456",    name: "Luxembourg" },
    MT: { dialCode: "+356", minLen: 8,  maxLen: 8,  mobileExample: "79123456",     name: "Malta" },
    NL: { dialCode: "+31",  minLen: 9,  maxLen: 9,  mobileExample: "612345678",    name: "Netherlands" },
    PL: { dialCode: "+48",  minLen: 9,  maxLen: 9,  mobileExample: "512345678",    name: "Poland" },
    PT: { dialCode: "+351", minLen: 9,  maxLen: 9,  mobileExample: "912345678",    name: "Portugal" },
    RO: { dialCode: "+40",  minLen: 9,  maxLen: 9,  mobileExample: "712345678",    name: "Romania" },
    SK: { dialCode: "+421", minLen: 9,  maxLen: 9,  mobileExample: "901234567",    name: "Slovakia" },
    SI: { dialCode: "+386", minLen: 8,  maxLen: 9,  mobileExample: "31234567",     name: "Slovenia" },
    ES: { dialCode: "+34",  minLen: 9,  maxLen: 9,  mobileExample: "612345678",    name: "Spain" },
    SE: { dialCode: "+46",  minLen: 7,  maxLen: 13, mobileExample: "701234567",    name: "Sweden" },
    GB: { dialCode: "+44",  minLen: 10, maxLen: 10, mobileExample: "7700900123",   name: "United Kingdom" },
};

// Ordered list for dropdowns — alphabetical by display name, with GB
// pinned to the top since that's the most common case for JLI.
export const EU_COUNTRY_OPTIONS: { code: string; name: string; dialCode: string }[] = (() => {
    const all = Object.entries(EU_PHONE_FORMATS).map(([code, f]) => ({
        code, name: f.name, dialCode: f.dialCode,
    }));
    const gb = all.find((c) => c.code === "GB")!;
    const rest = all.filter((c) => c.code !== "GB").sort((a, b) => a.name.localeCompare(b.name));
    return [gb, ...rest];
})();

export const dialCodeForCountry = (countryIso: string | null | undefined): string | null => {
    const iso = String(countryIso ?? "").trim().toUpperCase();
    return EU_PHONE_FORMATS[iso]?.dialCode ?? null;
};

const stripNonDigitsAndPlus = (s: unknown) =>
    String(s ?? "").replace(/[^\d+]/g, "");

const stripTrunkZero = (national: string) =>
    national.length > 1 && national.startsWith("0") ? national.slice(1) : national;

// Reverse lookup: "+36" → "HU". Null for an unknown / empty dial code.
export const isoForDialCode = (dialCode: string | null | undefined): string | null => {
    const raw = String(dialCode ?? "").replace(/[^\d]/g, "");
    if (!raw) return null;
    const hit = Object.entries(EU_PHONE_FORMATS).find(([, f]) => f.dialCode === "+" + raw);
    return hit ? hit[0] : null;
};

// Strict-validate the national portion against the country's
// configured length range. Returns true ⇔ digits-only AND length in
// range. For an unknown ISO we accept ≥6 digits (loose pass-through).
export const isValidNational = (national: string, countryIso: string | null | undefined): boolean => {
    const iso = String(countryIso ?? "").trim().toUpperCase();
    const cfg = EU_PHONE_FORMATS[iso];
    if (!/^\d+$/.test(national)) return false;
    if (!cfg) return national.length >= 6;
    return national.length >= cfg.minLen && national.length <= cfg.maxLen;
};

// Validate a national number the way the BE stores it: the dial code
// the operator picked decides the expected length, not the address
// country. A Hungarian buyer with a +36 mobile and a German billing
// address is valid (the BE trusts an explicit dial code — path (a) in
// orders.controller normaliseAddrPhone). Only when no dial code is
// selected, or it is one we don't know, do we fall back to the
// address country. Strips a dial code the operator typed into the
// number box ("+36 20…" → "20…") and a trunk zero before measuring,
// mirroring what the BE will store.
export const isValidNationalForDial = (
    rawNumber: string,
    dialCode: string | null | undefined,
    countryIso: string | null | undefined,
): boolean => {
    const dialIso = isoForDialCode(dialCode);
    let national = String(rawNumber ?? "").replace(/\D/g, "");
    if (dialIso) {
        const cfg = EU_PHONE_FORMATS[dialIso];
        const cc = cfg.dialCode.slice(1);
        if (national.startsWith(cc) && national.length > cfg.maxLen) national = national.slice(cc.length);
    }
    national = stripTrunkZero(national);
    return isValidNational(national, dialIso ?? countryIso);
};

// Normalise an arbitrary user input + ISO country into {dialCode, national}.
// Same algorithm as the BE; see phoneFormat.js for the rationale.
export const normalize = (
    input: string | null | undefined,
    countryIso: string | null | undefined,
): { valid: true; dialCode: string; national: string } | { valid: false; reason: string } => {
    if (input == null || input === "") return { valid: false, reason: "empty" };
    const iso = String(countryIso ?? "").trim().toUpperCase();
    const cfg = EU_PHONE_FORMATS[iso];
    const digits = stripNonDigitsAndPlus(input);
    if (!digits) return { valid: false, reason: "no_digits" };

    const tryStrip = (d: string, dialCode: string) => {
        const cc = dialCode.replace(/^\+/, "");
        if (d.startsWith("+" + cc)) return d.slice(1 + cc.length);
        if (d.startsWith("00" + cc)) return d.slice(2 + cc.length);
        if (d.startsWith(cc)) return d.slice(cc.length);
        return null;
    };

    if (cfg) {
        let national = tryStrip(digits, cfg.dialCode);
        if (national == null) national = digits.replace(/^\+/, "");
        national = stripTrunkZero(national);
        if (!/^\d+$/.test(national)) return { valid: false, reason: "non_digits" };
        if (national.length < cfg.minLen)
            return { valid: false, reason: `too_short_for_${iso}` };
        if (national.length > cfg.maxLen)
            return { valid: false, reason: `too_long_for_${iso}` };
        return { valid: true, dialCode: cfg.dialCode, national };
    }
    return { valid: false, reason: "country_required" };
};

export const formatE164 = (
    dialCode: string | null | undefined,
    national: string | null | undefined,
): string | null => {
    if (!dialCode || !national) return null;
    return `${dialCode}${national}`;
};
